import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { customerApi, getCustomerToken, clearCustomerSession } from '../../services/customerAuth'
import { useLanguage } from '../../context/LanguageContext'

function fmtShort(d) {
  if (!d) return ''
  try { return new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) }
  catch { return String(d) }
}

// Translated status-badge labels via a hook so re-renders track language changes.
function useStatusBadge() {
  const { t } = useLanguage()
  return function (b) {
    if (b.Status === 'needs_attention') return { bg: '#fef3c7', fg: '#92400e', label: t('sp_status_attention', 'Attention') }
    if (b.Status === 'cancelled') return { bg: '#fee2e2', fg: '#991b1b', label: t('sp_status_cancelled', 'Cancelled') }
    if (b.Status === 'confirmed' || (b.Status === 'pending' && b.HotelConfirmed)) return { bg: '#dcfce7', fg: '#166534', label: t('sp_status_confirmed', 'Confirmed') }
    return { bg: '#f1f5f9', fg: '#475569', label: t('sp_status_pending', 'Pending') }
  }
}

export default function MyBookings() {
  const { t } = useLanguage()
  const statusBadge = useStatusBadge()
  const [bookings, setBookings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (!getCustomerToken()) { navigate('/customer/login'); return }
    customerApi.get('/customer/my-bookings')
      .then(({ data }) => setBookings(data.bookings || []))
      .catch(err => {
        if (err.response?.status === 401) {
          clearCustomerSession()
          navigate('/customer/login')
        } else setError(err.response?.data?.message || err.message)
      })
      .finally(() => setLoading(false))
  }, [navigate])

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>{t('sp_loading_short', 'Loading…')}</div>

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <div style={{ background: '#153230', color: '#fff', padding: '18px 20px' }}>
        <div style={{ maxWidth: 720, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 800, fontSize: 18 }}>{t('sp_my_bookings_title', 'My Bookings')}</div>
          <button onClick={() => { clearCustomerSession(); navigate('/customer/login') }}
            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', padding: '6px 12px', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
            {t('sp_log_out', 'Log out')}
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '20px 16px 40px' }}>
        {error && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 6, marginBottom: 16 }}>{error}</div>}

        {bookings.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', background: '#fff', borderRadius: 12, color: '#64748b' }}>
            {t('sp_no_trips_yet', 'You don\'t have any trips yet.')}
          </div>
        )}

        {bookings.map(b => {
          const badge = statusBadge(b)
          return (
            <Link key={b.ID_NO} to={`/my-trip/${b.ID_NO}`} style={{
              display: 'block', background: '#fff', borderRadius: 12, padding: 20,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb',
              marginBottom: 12, textDecoration: 'none', color: 'inherit',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#153230' }}>{b.PackageName || `${t('sp_booking_label', 'Booking')} #${b.ID_NO}`}</div>
                  <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
                    {b.CourseName}{b.IsMultiCourse ? t('sp_plus_partners', ' + partners') : ''} · {b.NumGolfers} {b.NumGolfers > 1 ? t('sp_golfers_lower', 'golfers') : t('sp_golfer_lower', 'golfer')}
                  </div>
                  <div style={{ fontSize: 13, color: '#334155', marginTop: 6 }}>{fmtShort(b.PlayDate)}</div>
                </div>
                <span style={{
                  padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                  background: badge.bg, color: badge.fg,
                }}>{badge.label}</span>
              </div>
              <div style={{ fontSize: 13, color: '#00d19f', marginTop: 10, fontWeight: 600 }}>{t('sp_view_trip_arrow', 'View trip →')}</div>
            </Link>
          )
        })}
      </div>
    </div>
  )
}
